// SMG Sports Management — content, positioning, and taxonomy.
// Copy in this file is the approved verbatim brief. Do not alter without sign-off.

export const SPORTS_LOGO =
  'https://customer-assets-jai6qajn.emergentagent.net/job_cinema-hub-693/artifacts/3fso0h88_SMG%20Sport%20Managemnt%20Logo.png';

// Palette extracted from the SMG Sports Management logo — used only inside sports surfaces.
export const SPORTS_PALETTE = {
  navy: '#0A1F3D',        // deep court navy (base)
  blue: '#0057B7',        // electric court blue (primary accent)
  green: '#22C55E',       // court green (secondary / hover)
  orange: '#FF7A00',      // pickleball orange (highlight)
  parentRed: '#FF3D2E',   // carry-through from parent brand
};

// Homepage integration block — approved verbatim.
export const SPORTS_HOME = {
  eyebrow: 'A NEW DIVISION OF SMG',
  headline: 'Where Athlete Performance Meets Brand Power.',
  body:
    'SMG Sports Management Group International provides athlete representation, sponsorship development, personal-brand strategy, and co-branded opportunities — with an initial focus on professional and emerging pickleball talent.',
  primaryCta: { label: 'EXPLORE SPORTS MANAGEMENT', to: '/sports-management' },
  secondaryCta: { label: 'SEEK REPRESENTATION', to: '/sports-management#athlete-inquiry' },
};

// Landing page hero + core sections — approved verbatim.
export const SPORTS_LANDING = {
  hero: {
    eyebrow: 'SMG SPORTS MANAGEMENT GROUP INTERNATIONAL',
    headline: 'We Represent More Than Athletes. We Build Athlete Brands.',
    supporting:
      'Pickleball-focused representation, sponsorship development, brand partnerships, media strategy, and career management — powered by the full resources of SMG Media Group.',
    primaryCta: { label: 'APPLY FOR REPRESENTATION', to: '#athlete-inquiry' },
    secondaryCta: { label: 'PARTNER WITH AN ATHLETE', to: '#brand-inquiry' },
  },
  lineage: {
    headline: 'Two decades in sports. One new chapter.',
    intro:
      'For more than two decades, our team has represented and worked alongside some of the most recognizable names in professional sports — from the NFL, MLB, combat sports and action sports to influential personalities shaping today\u2019s culture.',
    paragraphs: [
      'In 2023, we stepped away from traditional sports representation following the sale of our former division. That transition gave us the opportunity to pursue something we believed was pure, energetic and without boundaries.',
      'With the launch of SMG Media Group, we have evolved once again \u2014 creating SMG Sports Management Group International, a dedicated pickleball-focused sports agency built for the future of the game.',
      'Backed by more than 20 years of experience building powerful brand relationships and helping curate some of the largest partnership opportunities in sports, SMG brings experience, foresight, technology and a new generation of ambitious agents to the fastest-growing sport in the world.',
      'Our mission is clear: to represent the athletes shaping pickleball\u2019s future, build their brands beyond the court and connect them with opportunities that match their talent, personality and long-term vision.',
    ],
    closer: 'This is more than athlete representation. This is the next generation of pickleball management.',
    proofChips: [
      { value: '20+', label: 'Years in sports representation' },
      { value: 'NFL · MLB', label: 'Combat sports · Action sports' },
      { value: '2023', label: 'Traditional division sold' },
      { value: '2026', label: 'SMG Sports Management launch' },
    ],
  },
  difference: {
    headline: 'Representation Meets Brand Development.',
    intro:
      "Most agencies negotiate deals. SMG also builds the athlete's identity, content, audience, sponsorship value, and long-term commercial platform.",
  },
  pickleball: {
    headline: 'Built for the Fastest-Growing Court in Sports.',
    body:
      'Built first for the rapidly growing world of pickleball — with the infrastructure to represent athletes across emerging and established sports.',
  },
  ecosystem: {
    headline: 'One Athlete. An Entire Growth System.',
    body:
      "Every represented athlete gains access to SMG's brand, creative, media, AI, partnership, and revenue-development capabilities.",
  },
};

// Four service pillars for the landing page.
export const SPORTS_SERVICES = [
  {
    num: '01',
    title: 'Representation',
    desc: 'Contract guidance, opportunity management, negotiation support, and professional career planning.',
  },
  {
    num: '02',
    title: 'Brand Development',
    desc: 'Athlete positioning, identity, social strategy, content development, and audience growth.',
  },
  {
    num: '03',
    title: 'Partnerships',
    desc: 'Sponsorships, endorsements, event partnerships, travel opportunities, and co-branded products.',
  },
  {
    num: '04',
    title: 'Career Growth',
    desc: 'Media opportunities, public appearances, business development, and long-term planning beyond competition.',
  },
];

// Who we represent — three tiers.
export const SPORTS_ROSTER_TIERS = [
  {
    title: 'Professional Athletes',
    desc:
      'Established competitors seeking strategic management, sponsorship development, and brand expansion.',
  },
  {
    title: 'Emerging & Amateur Athletes',
    desc:
      'High-potential players building competitive résumés, visibility, and professional opportunities.',
  },
  {
    title: 'Youth & NIL Talent',
    desc:
      'Young athletes and families seeking responsible guidance, brand development, and qualified NIL opportunities. Parent or legal guardian required for all youth inquiries.',
  },
];

// Full capability list (used in ecosystem section + JSON-LD offerCatalog).
export const SPORTS_CAPABILITIES = [
  'Athlete representation',
  'Sponsorship and endorsement development',
  'Personal-brand strategy',
  'Social-media and content development',
  'NIL opportunity development',
  'Public appearances',
  'Event and travel partnerships',
  'Co-branded merchandise and product collaborations',
  'Media, podcast, and broadcast opportunities',
  'Post-career business development',
];

export const PARTNERSHIP_TYPES = [
  'Sponsorship',
  'Athlete Ambassador',
  'Event Partnership',
  'Travel Partnership',
  'Product Collaboration',
  'Media / Content',
  'Other',
];

export const BUDGET_RANGES = [
  'Under $25K',
  '$25K – $75K',
  '$75K – $250K',
  '$250K – $1M',
  '$1M+',
  'Not yet defined',
];

export const AGE_RANGES = ['Under 18', '18-24', '25-34', '35+'];
export const LEVELS = ['Professional', 'Emerging Pro', 'Amateur', 'Youth / NIL'];

// Division contact — used across footer, homepage contact, and sports page.
export const SPORTS_DIVISION_CONTACT = {
  name: 'SMG Sports Management Group International',
  short: 'SMG Sports Management',
  email: 'repme@smgsportsmgt.com',
  phone: '+1 (619) 552-4677',
  phoneHref: 'tel:+16195524677',
  location: 'A Division of SMG Media Group · Carlsbad, CA',
};

// Team roster — SMG Sports Management Group International agents.
export const SPORTS_TEAM = [
  {
    slug: 'jacob-lee',
    name: 'Jacob Lee',
    role: 'Athlete Agent · Pickleball',
    email: 'jacob@smgsportsmgt.com',
    photo:
      'https://customer-assets-jai6qajn.emergentagent.net/job_cinema-hub-693/artifacts/4mxgk0uk_Jacob%20Lee%20Photos.webp',
    tag: 'THE NEXT GENERATION OF AGENT',
    bio: [
      'Jacob Lee returns home to SMG after initially spending a summer with our former agency as an intern, where he gained hands-on experience in brand activation, streaming content development and athlete representation.',
      'Today, Jacob has placed his focus entirely on the pickleball court. He brings a strong understanding of the game, athlete branding and the expanding opportunities available through sponsorships, endorsements, content and strategic brand integration.',
      'As a member of the SMG Sports Management Group International team, Jacob will focus on identifying, developing and representing pickleball players of all ages and competitive levels. His mission is to help each athlete build a distinctive personal brand, secure meaningful opportunities and establish a successful future both on and beyond the court.',
      'With his energy, industry experience and passion for the continued growth of pickleball, Jacob represents the next generation of athlete management \u2014 and an important addition to SMG\u2019s pickleball-focused division.',
    ],
  },
];
